import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TranslationGroup, TranslationPage } from '../api/types';
import { useApi } from '../lib/use-api';
import { languageName, localeCompare } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { CreateTranslationDialog } from '../components/CreateTranslationDialog';
import { SUPPORTED_LOCALES } from '../i18n/config';

const PAGE_SIZE = 25;

/**
 * Ma trận bản dịch — plan §5.1b.
 *
 * Mỗi dòng là một `translation_group_id`, mỗi cột là một ngôn ngữ. Ô có bài
 * thì hiện tiêu đề; ô trống thì hiện nút tạo bản dịch.
 *
 * Cột ngôn ngữ = tập ngôn ngữ giao diện HỢP với mọi ngôn ngữ đã xuất hiện
 * trong dữ liệu. Chỉ lấy theo dữ liệu thì tenant chưa dịch gì sẽ thấy bảng
 * một cột và không có chỗ nào để bấm tạo bản dịch.
 */
export function TranslationsPage(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';

  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState<
    { group: TranslationGroup; language: string } | undefined
  >(undefined);

  const query = useMemo(() => ({ limit: PAGE_SIZE, offset }), [offset]);
  const page = useApi<TranslationPage>('/api/admin/translations', { query });

  const languages = useMemo(() => {
    const set = new Set<string>(SUPPORTED_LOCALES);
    for (const group of page.data?.groups ?? []) {
      for (const variant of group.variants) set.add(variant.language);
    }
    return [...set].sort(localeCompare(locale));
  }, [page.data, locale]);

  return (
    <section>
      <h1>{t('translation.heading')}</h1>
      <p className="hint">{t('translation.intro')}</p>

      {page.loading ? <Loading /> : null}
      {page.error ? <ErrorNotice error={page.error} onRetry={page.reload} /> : null}

      {page.data && !page.loading ? (
        page.data.groups.length === 0 ? (
          <EmptyNotice />
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('translation.column.title')}</th>
                  <th scope="col">{t('translation.column.contentType')}</th>
                  {languages.map((language) => (
                    <th key={language} scope="col">
                      {languageName(language, locale)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.data.groups.map((group) => {
                  const source =
                    group.variants.find((variant) => variant.isSource) ?? group.variants[0];
                  return (
                    <tr key={group.translationGroupId}>
                      <td>{source?.title ?? '—'}</td>
                      <td className="mono">{group.mainLibrary}</td>
                      {languages.map((language) => {
                        const variant = group.variants.find((item) => item.language === language);
                        return (
                          <td key={language}>
                            {variant ? (
                              <span className="matrix-cell">
                                {variant.title}
                                {variant.isSource ? (
                                  <span className="badge">{t('translation.source')}</span>
                                ) : null}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="link-button"
                                onClick={() => setCreating({ group, language })}
                              >
                                {t('translation.create')}
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Pagination
              total={page.data.total}
              limit={page.data.limit}
              offset={page.data.offset}
              onChange={setOffset}
            />
          </>
        )
      ) : null}

      {creating ? (
        <CreateTranslationDialog
          group={creating.group}
          targetLanguage={creating.language}
          onClose={() => setCreating(undefined)}
          onCreated={() => {
            setCreating(undefined);
            page.reload();
          }}
        />
      ) : null}
    </section>
  );
}
